import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import { acquireToken } from '../../features/common/auth/msal-instance';
import { store } from '../../store';
import { apiErrorHandler } from './api-error-handler';
import { getAppSettings } from '../settings-service';

// Central place for cancellation checks
export function isApiCancel(error: unknown): boolean {
  return axios.isCancel(error);
}

export function getScopes(): string[] {
  const appSettings = store.getState().appSettings.data;
  if (!appSettings) {
    return [];
  }

  return [`api://${appSettings.auth.aadClientId}/Default`];
}

interface IApiMap {
  [key: string]: AxiosInstance;
}
const _api: IApiMap = {};

interface IApiParameters {
  ignoreTenant?: boolean | undefined;
  ignoreAllErrors?: boolean | undefined;
  ignoreErrorStatuses?: number[];
  url?: string;
}
const getAxios = (baseUrl: string, parameters: IApiParameters | null = null): AxiosInstance => {
  const key = `${baseUrl}_${parameters ? JSON.stringify(parameters) : 'default'}`;
  if (_api[key]) {
    return _api[key];
  }

  const api: AxiosInstance = axios.create();

  api.interceptors.request.use(
    async (config: InternalAxiosRequestConfig & { skipAuth?: boolean }) => {
      config.baseURL = baseUrl;

      if (config.headers && !config.headers['Content-Type']) {
        config.headers['Content-Type'] = 'application/json';
      }

      if (config.skipAuth) {
        return config;
      }

      const accessToken = await acquireToken(getScopes());
      if (config.headers && accessToken) {
        config.headers['Authorization'] = 'Bearer ' + accessToken;
      }

      return config;
    },
    (error) => {
      Promise.reject(error);
    },
  );

  api.interceptors.response.use(
    (next) => {
      return Promise.resolve(next);
    },
    (err: AxiosError) => {
      console.log(err);
      if (!parameters?.ignoreAllErrors && !parameters?.ignoreErrorStatuses?.includes(err.response?.status || 0)) {
        apiErrorHandler(err);
      }

      return Promise.reject(err);
    },
  );

  _api[key] = api;
  return api;
};

const getApiUrl = (): Promise<string> => {
  return getAppSettings().then((appSettings) => appSettings.api_url);
};

const getCameraApiUrl = (): Promise<string> => {
  return getAppSettings().then((appSettings) => appSettings.camera_api_url || '');
};

export const getApi = async (parameters: IApiParameters | null = null): Promise<AxiosInstance> => {
  const apiUrl = await getApiUrl();
  return getAxios(apiUrl, parameters);
};

export const getCameraApi = async (parameters: IApiParameters | null = null): Promise<AxiosInstance> => {
  const apiUrl = await getCameraApiUrl();
  return getAxios(apiUrl, parameters);
};
